import type { ReactElement } from 'react'

import type { RelatorioSympla } from '../types'

import { cn } from '@/lib/cn'
import { inteiro } from '@/lib/formato'

/** 'YYYY-MM-DD' → 'DD/MM' sem passar por Date — date-only não tem fuso. */
function diaCurto(dia: string): string {
  return dia.slice(8) + '/' + dia.slice(5, 7)
}

export interface CurvaVendasProps {
  curva: RelatorioSympla['curvaVendas']
  /** Escala de telão: barras mais altas e tipografia maior pro canvas de 2500px. */
  telao?: boolean
}

/**
 * Curva de vendas do evento: barras de ingressos por dia com o acumulado no
 * topo. Primitivo próprio (sem lib de gráfico), como o restante do painel.
 */
export function CurvaVendas({ curva, telao = false }: CurvaVendasProps): ReactElement {
  if (curva.length === 0) {
    return (
      <p className={cn('text-muted-foreground', telao ? 'text-xl' : 'text-sm')}>
        Nenhuma venda registrada ainda — a curva nasce com o primeiro pedido aprovado.
      </p>
    )
  }

  const max = Math.max(...curva.map((p) => p.ingressos), 1)
  const acumulado = curva[curva.length - 1]!.acumulado

  return (
    <div>
      <p className={cn('text-muted-foreground mb-3', telao ? 'text-xl' : 'text-sm')}>
        {inteiro(acumulado)} ingresso(s) acumulados de {diaCurto(curva[0]!.dia)} até{' '}
        {diaCurto(curva[curva.length - 1]!.dia)}
      </p>
      <div
        className={cn(
          'scrollbar-fino flex items-end gap-1 overflow-x-auto pb-1',
          telao ? 'h-80' : 'h-40',
        )}
      >
        {curva.map((ponto) => (
          <div
            key={ponto.dia}
            className="flex h-full min-w-[1.25rem] flex-1 flex-col items-center justify-end gap-1"
            title={`${diaCurto(ponto.dia)}: ${inteiro(ponto.ingressos)} ingresso(s) · acumulado ${inteiro(ponto.acumulado)}`}
          >
            {ponto.ingressos > 0 ? (
              <span
                className={cn(
                  'text-muted-foreground tabular-nums',
                  telao ? 'text-base' : 'text-[10px]',
                )}
              >
                {inteiro(ponto.ingressos)}
              </span>
            ) : null}
            <div
              data-testid={`barra-${ponto.dia}`}
              className="bg-primary w-full rounded-t"
              style={{ height: `${Math.max(2, (100 * ponto.ingressos) / max)}%` }}
            />
            <span className={cn('text-muted-foreground', telao ? 'text-base' : 'text-[10px]')}>
              {diaCurto(ponto.dia)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
